import { useCallback, useEffect, useRef, useState } from 'react';
import type { AudioProcessor } from '@/lib/audioProcessor';
import { PHENOMENA, PALETTES, type PhenomenonId, type PsyState } from '@/lib/psychedelic/constants';

type Status = 'idle' | 'mic' | 'file';

/**
 * Controls for the psychedelic visualizer: phenomenon selector (each tied to a
 * form constant + ASC dimension), palette (warm psychedelic / cold dissociative),
 * dose & reactivity (entropy/REBUS scaling), post-Fx toggle, and audio I/O.
 * Live meters expose the 5D-ASC dimensions being driven by the audio.
 */
export function PsychedelicControls({
  processor,
  psy,
  setPsy,
}: {
  processor: AudioProcessor;
  psy: PsyState;
  setPsy: (p: Partial<PsyState>) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [trackName, setTrackName] = useState('');
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dims, setDims] = useState({ oceanic: 0, restructuring: 0, syn: 0.5, diss: 0, vig: 1 });

  // Reflect processor state into React.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      setStatus(processor.active ? (processor.sourceType === 'file' ? 'file' : 'mic') : 'idle');
      setPlaying(processor.isPlaying);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [processor]);

  // Poll the ASC dimension meters via the texture + processor internals.
  // We approximate by reading the spectrum texture's alpha (dissonance) and
  // derived band ratios — cheap and good enough for a live readout.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const a = processor as unknown as {
        smoothCentroid?: number; smoothFlux?: number; smoothDissonance?: number;
        smoothSub?: number; smoothBass?: number; smoothTreble?: number; smoothLevel?: number;
      };
      const data = (processor.texture.image as Uint8Array) ?? null;
      let bass = 0, treble = 0;
      if (data && data.length > 8) {
        bass = data[0] / 255;
        treble = data[Math.floor(data.length * 0.75)] / 255;
      }
      const diss = (a.smoothDissonance ?? 0);
      const cen = (a.smoothCentroid ?? 0.5);
      const lvl = (a.smoothLevel ?? 0);
      setDims({
        oceanic: Math.min(1, ((a.smoothSub ?? 0) * 1.5 + (a.smoothBass ?? 0)) / (treble + 0.3)),
        restructuring: Math.min(1, (a.smoothFlux ?? 0) * 0.8 + bass * 0.4),
        syn: cen,
        diss,
        vig: Math.max(0, 1 - lvl * 1.5),
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [processor]);

  const handleMic = useCallback(async () => {
    setError(null);
    if (processor.active && processor.sourceType === 'mic') { processor.stop(); setTrackName(''); return; }
    try { setTrackName(''); await processor.startMic(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Microphone access failed'); }
  }, [processor]);

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null); setTrackName(f.name);
    try { await processor.startFile(f); }
    catch (err) { setError(err instanceof Error ? err.message : 'Failed to load track'); }
    e.target.value = '';
  }, [processor]);

  const handlePlay = useCallback(() => processor.togglePlay(), [processor]);

  const btn = (active: boolean, activeCls: string) =>
    `w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
      active ? activeCls : 'bg-white/[0.04] border-white/10 text-white/40 hover:text-white/70 hover:border-white/20'
    }`;

  return (
    <>
      <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={handleFile} />

      {/* ── Top: phenomenon selector + palette ── */}
      <div className="fixed top-0 inset-x-0 z-50 pointer-events-none">
        <div className="bg-black/50 backdrop-blur-xl border-b border-white/[0.06] pointer-events-auto">
          <div className="flex items-center gap-1.5 px-3 py-2 overflow-x-auto scrollbar-none">
            {PHENOMENA.map((ph) => (
              <button
                key={ph.id}
                onClick={() => setPsy({ phenomenon: ph.id as PhenomenonId })}
                title={`${ph.formConstant} · ${ph.ascDimension}\n${ph.note}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] tracking-wider uppercase font-medium border transition-all duration-300 ${
                  psy.phenomenon === ph.id
                    ? 'bg-fuchsia-500/20 border-fuchsia-400/40 text-fuchsia-200 shadow-[0_0_14px_rgba(217,70,239,0.25)]'
                    : 'bg-white/[0.03] border-white/[0.08] text-white/40 hover:text-white/70 hover:border-white/15'
                }`}
              >
                {ph.name}
              </button>
            ))}

            <div className="w-px h-5 bg-white/10 mx-1 shrink-0" />

            {PALETTES.map((pal, i) => (
              <button
                key={pal.id}
                onClick={() => setPsy({ paletteIndex: i })}
                title={`${pal.name} (${pal.family})`}
                className={`shrink-0 w-7 h-7 rounded-full border transition-all duration-300 ${
                  psy.paletteIndex === i ? 'border-white/60 scale-110' : 'border-white/15 hover:border-white/35'
                }`}
                style={{ background: `linear-gradient(135deg, rgb(${pal.a.join(',')}), rgb(${pal.b.join(',')}), rgb(${pal.c.join(',')}))` }}
              />
            ))}
          </div>

          {/* Phenomenon note */}
          <div className="px-3 pb-1.5 -mt-0.5">
            <p className="text-[9px] text-white/30 font-mono truncate">
              {PHENOMENA.find((p) => p.id === psy.phenomenon)?.note}
            </p>
          </div>
        </div>
      </div>

      {/* ── Bottom: audio I/O + dose + ASC meters ── */}
      <div className="fixed bottom-0 inset-x-0 z-50 pointer-events-none">
        <div className="h-8 sm:h-12 bg-gradient-to-t from-black/80 via-black/30 to-transparent pointer-events-none" />
        <div className="bg-black/60 backdrop-blur-xl border-t border-white/[0.06] pointer-events-auto"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="flex items-center gap-2 px-3 py-2.5">
            {/* mic */}
            <button onClick={handleMic} title={status === 'mic' ? 'Stop mic' : 'Start mic'}
              className={btn(status === 'mic', 'bg-purple-500/25 border-purple-400/40 text-purple-300 shadow-[0_0_14px_rgba(168,85,247,0.3)]')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" x2="12" y1="19" y2="22" />
              </svg>
            </button>
            {/* upload */}
            <button onClick={() => fileRef.current?.click()} title="Upload track"
              className={btn(status === 'file', 'bg-pink-500/25 border-pink-400/40 text-pink-300 shadow-[0_0_14px_rgba(236,72,153,0.3)]')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" />
              </svg>
            </button>
            {/* play */}
            <button onClick={handlePlay} disabled={status !== 'file'}
              title={status !== 'file' ? 'Upload a track first' : playing ? 'Pause' : 'Play'}
              className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
                status !== 'file' ? 'bg-white/[0.02] border-white/[0.06] text-white/25 cursor-not-allowed'
                  : playing ? 'bg-emerald-500/25 border-emerald-400/40 text-emerald-300 shadow-[0_0_14px_rgba(16,185,129,0.3)]'
                  : 'bg-white/[0.04] border-white/10 text-white/40 hover:text-white/70 hover:border-white/20'
              }`}>
              {playing ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4.5v15a1 1 0 0 0 1.55.83l12-7.5a1 1 0 0 0 0-1.66l-12-7.5A1 1 0 0 0 7 4.5Z" /></svg>
              )}
            </button>

            <div className="flex-1 min-w-0 px-1">
              {error ? <span className="text-[10px] text-red-400/80 font-mono truncate">{error}</span>
                : trackName ? <span className="text-[10px] text-white/40 font-mono truncate block">{trackName}</span>
                : status === 'idle' ? <span className="text-[10px] text-white/30 tracking-wider uppercase">Mic or upload to begin</span>
                : <span className="text-[10px] text-white/50 tracking-wider uppercase">{status === 'mic' ? 'Live microphone' : 'Track loaded'}</span>}
            </div>

            {/* dose + reactivity sliders */}
            <Slider label="Dose" value={psy.dose} onChange={(v) => setPsy({ dose: v })} accent />
            <Slider label="Reactivity" value={psy.reactivity} onChange={(v) => setPsy({ reactivity: v })} />

            <button onClick={() => setPsy({ postFx: !psy.postFx })}
              className={`shrink-0 px-2.5 h-9 rounded-full text-[10px] tracking-wider uppercase font-medium border transition-all ${
                psy.postFx ? 'bg-white/[0.12] border-white/25 text-white/90' : 'bg-white/[0.03] border-white/[0.08] text-white/40'
              }`}>
              FX
            </button>

            {status !== 'idle' && (
              <button onClick={() => { processor.stop(); setTrackName(''); }}
                className="shrink-0 px-2.5 h-9 rounded-full text-[10px] tracking-wider uppercase font-medium border bg-red-500/20 border-red-400/40 text-red-300 hover:bg-red-500/30 transition-all">
                Stop
              </button>
            )}
          </div>

          {/* ASC dimension meters */}
          {status !== 'idle' && (
            <div className="flex items-center gap-3 px-3 pb-2 overflow-x-auto scrollbar-none">
              <Dim label="Oceanic" value={dims.oceanic} color="from-indigo-500 to-blue-400" />
              <Dim label="Restructure" value={dims.restructuring} color="from-fuchsia-500 to-pink-400" />
              <Dim label="Synesthesia" value={dims.syn} color="from-amber-500 to-yellow-400" />
              <Dim label="Dissolution" value={dims.diss} color="from-red-500 to-orange-400" />
              <Dim label="Vigilance↓" value={dims.vig} color="from-slate-500 to-slate-300" />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Slider({ label, value, onChange, accent }: { label: string; value: number; onChange: (v: number) => void; accent?: boolean }) {
  const pct = value * 100;
  return (
    <div className="hidden md:flex flex-col gap-1 w-24 shrink-0">
      <div className="flex justify-between items-baseline px-0.5">
        <span className="text-[9px] tracking-[0.12em] uppercase text-white/50 font-medium">{label}</span>
        <span className="text-[9px] text-white/30 tabular-nums font-mono">{value.toFixed(2)}</span>
      </div>
      <div className="relative h-5 flex items-center group">
        <div className="absolute inset-x-0 h-[2px] rounded-full bg-white/[0.06]" />
        <div className="absolute left-0 h-[2px] rounded-full transition-all duration-75"
          style={{ width: `${pct}%`, background: accent ? 'linear-gradient(90deg, rgba(217,70,239,0.7), rgba(168,85,247,0.9))' : 'linear-gradient(90deg, rgba(255,255,255,0.15), rgba(255,255,255,0.35))' }} />
        <input type="range" min={0} max={1} step={0.01} value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
        <div className="absolute w-3 h-3 rounded-full border border-white/20 pointer-events-none transition-all duration-75"
          style={{ left: `calc(${pct}% - 6px)`, background: accent ? 'rgba(217,70,239,0.9)' : 'rgba(255,255,255,0.6)' }} />
      </div>
    </div>
  );
}

function Dim({ label, value, color }: { label: string; value: number; color: string }) {
  const pct = Math.min(100, Math.max(0, value * 100));
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-[8px] tracking-[0.12em] uppercase text-white/35 font-mono w-16">{label}</span>
      <div className="relative h-1 w-16 rounded-full bg-white/[0.06] overflow-hidden">
        <div className={`absolute left-0 inset-y-0 rounded-full bg-gradient-to-r ${color} transition-[width] duration-100`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
